import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ACL_RELATIONSHIPS from "acl-relationships";
import {
  addCustomFieldIssues,
  BaseForm,
  buildCustomFieldDefaults,
  buildCustomFieldSelection,
  customFieldsPayload,
  customFieldsSchema,
  CustomFieldsSection,
  FormCombobox,
  FormInput,
  FormSkeleton,
  FormToggle,
  useCustomFieldConfigs,
  useCustomFieldGroups,
} from "components/BaseForm";
import Validate from "components/Validate";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import { useUserFormOptions, useUserMutations } from "hooks/useUsersTable";
import UserService from "services/User";
import UserPhoneNumberFields from "routes/admin/components/UserPhoneNumberFields";
import { USERS_PATH } from "routes/admin/components/UsersTable/UsersTableConstants";

const CUSTOM_FIELD_MODEL = "User";

const buildSchema = configs =>
  z
    .object({
      full_name: z.string().trim().min(1, "Name is required"),
      email: z
        .string()
        .trim()
        .min(1, "Email is required")
        .refine(Validate.email, "Please enter a valid email address"),
      title: z.string(),
      phone_numbers: z.array(
        z.object({
          id: z.number().optional(),
          number: z.string().nullable(),
          ext: z.string(),
          type: z.number().nullable(),
          is_primary: z.boolean(),
          enabled: z.boolean(),
          is_callable: z.boolean(),
        })
      ),
      role_id: z.number({
        required_error: "A role is required",
        invalid_type_error: "A role is required",
      }),
      region_id: z.number().nullable(),
      sso_bypass: z.boolean(),
      custom_fields: customFieldsSchema,
    })
    .superRefine((values, ctx) => {
      addCustomFieldIssues(configs, values.custom_fields, ctx);
    });

const buildUserDefaults = (user, configs) => ({
  full_name: user?.full_name ?? "",
  email: user?.email ?? "",
  title: user?.title ?? "",
  phone_numbers: (user?.phone_numbers ?? []).map(p => ({
    id: p.id,
    number: p.number,
    ext: p.ext ?? "",
    type: p.type,
    is_primary: !!p.is_primary,
    enabled: !!p.enabled,
    is_callable: !!p.is_callable,
  })),
  role_id: user?.role?.id ?? null,
  region_id: user?.region_id ?? null,
  sso_bypass: !!user?.sso_bypass,
  custom_fields: buildCustomFieldDefaults(configs, user?.custom_fields),
});

const toOptions = records =>
  (records ?? []).map(({ id, name }) => ({ key: id, value: id, text: name }));

const UserFormInner = ({
  user,
  configs,
  customFieldGroups,
  options,
  pageTitle,
}) => {
  const navigate = useNavigate();
  const isEdit = user != null;
  const { create, update } = useUserMutations();
  const [selection, setSelection] = useState(() =>
    buildCustomFieldSelection(
      configs,
      isEdit ? (user.custom_fields ?? {}) : null
    )
  );

  const schema = useMemo(() => buildSchema(configs), [configs]);
  const defaultValues = useMemo(
    () => buildUserDefaults(user, configs),
    [user, configs]
  );
  const roleOptions = useMemo(() => toOptions(options.roles), [options.roles]);
  const regionOptions = useMemo(
    () => toOptions(options.regions),
    [options.regions]
  );

  const handleSubmit = async values => {
    const data = {
      email: values.email,
      fullName: values.full_name,
      title: values.title,
      phoneNumbers: values.phone_numbers.filter(p => p.number?.trim()),
      role: values.role_id,
      region: values.region_id,
      // The form has no group field, but the update endpoint resets the group
      // whenever it is missing, so an edit resends the stored value.
      groupId: user?.group_id,
      ssoBypass: values.sso_bypass,
      customFields: customFieldsPayload(configs, values.custom_fields),
    };
    if (isEdit) {
      await update.mutateAsync({ id: user.id, data });
    } else {
      await create.mutateAsync(data);
    }
  };

  const handleSuccess = () => {
    toast.success(isEdit ? "User updated" : "User created");
    navigate(USERS_PATH);
  };

  return (
    <BaseForm
      enablePageTitle
      enablePageTitleActions
      pageTitle={pageTitle}
      pageSubtitle={isEdit ? user.full_name : undefined}
      backLink={{ to: USERS_PATH, label: "Back to Users" }}
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onCancel={() => navigate(USERS_PATH)}
      submitButtonText={isEdit ? "Save User" : "Create User"}
    >
      <div className="tw:flex tw:flex-col tw:gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
              <FormInput name="full_name" label="Name" required />
              <FormInput name="email" label="Email" type="email" required />
              <FormInput name="title" label="Title" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Access</CardTitle>
          </CardHeader>
          <CardContent className="tw:flex tw:flex-col tw:gap-5">
            <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
              <FormCombobox
                name="role_id"
                label="Role"
                required
                clearable
                options={roleOptions}
              />
              <FormCombobox
                name="region_id"
                label="Region"
                clearable
                options={regionOptions}
              />
            </div>
            {UserService.isAdmin() && (
              <FormToggle
                name="sso_bypass"
                label="SSO Bypass (Allow password login)"
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Phone Numbers</CardTitle>
          </CardHeader>
          <CardContent>
            <UserPhoneNumberFields />
          </CardContent>
        </Card>

        {selection.length > 0 && (
          <CustomFieldsSection
            selection={selection}
            setSelection={setSelection}
            groups={customFieldGroups}
          />
        )}
      </div>
    </BaseForm>
  );
};

UserFormInner.propTypes = {
  user: PropTypes.object,
  configs: PropTypes.array.isRequired,
  customFieldGroups: PropTypes.array,
  options: PropTypes.shape({
    roles: PropTypes.array,
    regions: PropTypes.array,
  }).isRequired,
  pageTitle: PropTypes.string.isRequired,
};

function UserForm({ mode }) {
  const { id } = useParams();
  const isEdit = mode === "edit";

  const canWrite = useAuthorized([
    isEdit
      ? ACL_RELATIONSHIPS.adminUser.edit
      : ACL_RELATIONSHIPS.adminUser.create,
  ]);

  const pageTitle = isEdit ? "Edit User" : "New User";
  usePageTitle(pageTitle);

  const {
    data: user,
    isPending: userPending,
    isError: userError,
  } = useQuery({
    queryKey: ["admin-user", id],
    queryFn: () => UserService.getUserAsAdmin(id),
    enabled: isEdit,
    retry: false,
    // The form reads its defaults once, on mount. A cached entry from an
    // earlier visit would mount it with pre-edit data, so the entry is
    // dropped as soon as this page unmounts.
    gcTime: 0,
  });
  const { roles, regions } = useUserFormOptions();
  const configs = useCustomFieldConfigs(CUSTOM_FIELD_MODEL);
  const customFieldGroups = useCustomFieldGroups(CUSTOM_FIELD_MODEL);

  if (!canWrite) {
    return <Navigate to={USERS_PATH} replace />;
  }

  if (userError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <p>{"Couldn't load this user. Please try again."}</p>
        <Link to={USERS_PATH}>Back to Users</Link>
      </div>
    );
  }

  const ready =
    (!isEdit || !userPending) &&
    roles.isFetched &&
    regions.isFetched &&
    configs.isFetched &&
    customFieldGroups.isFetched;

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-center tw:font-sans tw:text-(--color-text-primary)">
      <div className="tw:flex tw:w-full tw:max-w-6xl tw:flex-col tw:gap-4">
        {ready ? (
          <UserFormInner
            user={isEdit ? user : null}
            configs={configs.data ?? []}
            customFieldGroups={customFieldGroups.data}
            options={{
              roles: roles.data,
              regions: regions.data,
            }}
            pageTitle={pageTitle}
          />
        ) : (
          <FormSkeleton sections={3} />
        )}
      </div>
    </div>
  );
}

UserForm.propTypes = {
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
};

export default UserForm;
